import type { ResearchProgressEvent } from '@raro/domain';
import { cx, Modal } from '@raro/ui';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

type Row = ResearchProgressEvent & { at: number };
const host = (url: string) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; } };
export const newProgressId = () => crypto.randomUUID();

/**
 * Roda uma pesquisa na web e espera o resultado. A pesquisa segue no servidor em segundo plano (leva minutos, mais do que
 * um proxy deixa uma requisição aberta); aqui a tela pergunta de tempos em tempos se terminou.
 */
export async function runResearch<T>(body: Record<string, unknown>, onStart: (progressId: string) => void): Promise<T> {
  const progressId = newProgressId();
  onStart(progressId);
  await api.post('/ai/research-fill', { ...body, progressId });
  return waitProgress<T>(progressId, { what: 'a pesquisa' });
}

/**
 * Espera um trabalho que a API faz em segundo plano (pesquisa, desenho, comparação) e devolve o resultado dele, que sai
 * em `GET ai/research-progress/:id` quando termina. `onSteps` recebe os passos novos enquanto isso.
 */
export async function waitProgress<T>(progressId: string, opts: { what?: string; onSteps?: (steps: { kind: string; text: string }[]) => void; maxMinutes?: number } = {}): Promise<T> {
  const what = opts.what ?? 'o trabalho';
  let misses = 0, seen = 0;
  for (let i = 0; i < (opts.maxMinutes ?? 10) * 30; i++) {
    await new Promise((r) => setTimeout(r, 2000));
    let p: { done: boolean; known: boolean; total: number; events: { kind: string; text: string }[]; result: T | null; error: string | null };
    try { p = await api.get(`/ai/research-progress/${progressId}?after=${opts.onSteps ? seen : 100000}`); } catch { if (++misses > 8) throw new Error(`perdi o contato com ${what}. tente de novo.`); continue; }
    misses = 0;
    if (!p.known) throw new Error(`${what} parou no servidor antes de terminar. tente de novo.`);
    if (opts.onSteps && p.events.length) { seen = p.total; opts.onSteps(p.events); }
    if (p.done) { if (p.error || !p.result) throw new Error(p.error ?? `${what} terminou sem resultado.`); return p.result; }
  }
  throw new Error(`${what} demorou demais. tente de novo.`);
}

/**
 * A pesquisa na web acontecendo: o que a Machine disse que ia procurar e as páginas que foi lendo.
 * A tela pergunta ao servidor a cada segundo (`GET ai/research-progress/:id`); é só acompanhamento, o resultado vem pela chamada da pesquisa.
 */
export function ResearchLive({ progressId, title, hint }: { progressId: string | null; title: string; hint?: string }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [seconds, setSeconds] = useState(0);
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!progressId) return;
    setRows([]); setSeconds(0);
    let stop = false; let total = 0;
    const started = Date.now();
    const tick = async () => {
      if (stop) return;
      setSeconds(Math.round((Date.now() - started) / 1000));
      try {
        const r = await api.get<{ events: Row[]; total: number }>(`/ai/research-progress/${progressId}?after=${total}`);
        if (!stop && r.events.length) { total = r.total; setRows((prev) => [...prev, ...r.events]); }
      } catch { /* acompanhamento nunca derruba a pesquisa */ }
      if (!stop) timer = window.setTimeout(tick, 1000);
    };
    let timer = window.setTimeout(tick, 400);
    return () => { stop = true; window.clearTimeout(timer); };
  }, [progressId]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [rows.length]);
  const pages = rows.filter((r) => r.kind === 'page').length;
  // os resultados de uma busca chegam em rajada: viram uma linha só, com os sites (um por domínio)
  type Group = { kind: 'row'; row: Row } | { kind: 'pages'; urls: string[]; hosts: { host: string; url: string; title: string }[] };
  const groups: Group[] = [];
  for (const r of rows) {
    if (r.kind !== 'page' || !r.url) { groups.push({ kind: 'row', row: r }); continue; }
    let g = groups[groups.length - 1];
    if (!g || g.kind !== 'pages') { g = { kind: 'pages', urls: [], hosts: [] }; groups.push(g); }
    g.urls.push(r.url);
    if (!g.hosts.some((h) => h.host === host(r.url!))) g.hosts.push({ host: host(r.url), url: r.url, title: r.text });
  }
  return (
    <Modal open={Boolean(progressId)} eyebrow="pesquisa na web" title={title} footer={<><span><span className="rr-live__pulse" />{seconds}s · {pages} resultado(s) vistos</span><span>{hint ?? 'costuma levar de um a dois minutos. pode esperar aqui.'}</span></>}>
      <div className="rr-live">
        {rows.length === 0 && <div className="rr-live__row rr-live__row--stage">chamando a Machine…</div>}
        {groups.map((g, i) => g.kind === 'pages' ? (
          <div key={i} className="rr-live__row rr-live__row--page">
            <span>{g.urls.length} resultado(s): {g.hosts.slice(0, 5).map((h, k) => <a key={h.url} href={h.url} target="_blank" rel="noreferrer" title={h.title}>{k ? ', ' : ''}{h.host}</a>)}{g.hosts.length > 5 ? ` e mais ${g.hosts.length - 5}` : ''}</span>
          </div>
        ) : (
          <div key={i} className={cx('rr-live__row', `rr-live__row--${g.row.kind}`)}><span>{g.row.kind === 'search' ? `buscando: ${g.row.text}` : g.row.text}</span></div>
        ))}
        <div ref={endRef} />
      </div>
    </Modal>
  );
}
